import { useState } from 'react';
import SongsTable from './SongsTable/SongsTable';
import { Meta as SongsTableMeta, SongsTableRow } from './SongsTable/helpers';
import styles from './SongsList.module.css';
import { ScrollView } from '@round/ui-kit';
import { Button, assets } from '@round/whitelabel-ui';
import { PageTitle } from '@round/whitelabel-ui';
import CreateSongModal from '../Modals/CreateSongModal/CreateSongModal';
import SongDataModal from '../SongDataModal/SongDataModal';
import UpdateSongModal from '../Modals/UpdateSongModal/UpdateSongModal';
import useUrlState from 'Hooks/useUrlState';
import { SearchInput } from '@round/whitelabel-ui';
import { creatorbase } from '@round/api';
import DeleteSongModal from '../Modals/DeleteSongModal/DeleteSongModal';
import { StylesConfig } from 'react-select';
import { Select } from '@round/whitelabel-ui';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import { isNumber } from 'utility/utility';
import useEffectWithPreviousDeps from 'Hooks/useEffectWithPreviousDeps';
import useSongsData from './useSongsData';

const { Plus: PlusIcon } = assets;

type UrlState = creatorbase.GetSongsParams & {
    songModal: number;
    status?: string;
};

const SongsList = () => {
    const { selectedClientId } = useClientSelect();
    const [urlState, setUrlState] = useUrlState<UrlState>();

    const { songs, musicSongs, timeSeries, isLoading, isError, isFetchingNextPage, hasNextPage, fetchNextPage } =
        useSongsData(
            {
                page_size: 25,
                search: urlState.search,
                status: urlState.status,
                ordering: '-created',
                client_id: selectedClientId ?? undefined,
            },
            { enabled: isNumber(selectedClientId) }
        );

    useEffectWithPreviousDeps(
        (prev) => {
            const [prevClientId] = prev ?? [];
            if (prevClientId && prevClientId !== selectedClientId) {
                setUrlState({ search: '', status: undefined });
            }
        },
        [selectedClientId, setUrlState]
    );

    const rows = songs.map<SongsTableRow>((song) => ({
        ...song,
        musicSong: musicSongs.data.find((m) => m.id === song.music_song_id),
        timeSeries: timeSeries.data.get(song.id) ?? null,
    }));

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const songIdForModal = urlState.songModal ? Number(urlState.songModal) : undefined;

    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [selectedSongRowToEdit, setSelectedSongRowToEdit] = useState<SongsTableRow | null>(null);

    const onClickEditSong = (song: SongsTableRow) => {
        setSelectedSongRowToEdit(song);
        setIsUpdateModalOpen(true);
    };

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [songRowToDelete, setSongRowToDelete] = useState<SongsTableRow | null>(null);

    const onClickDeleteSong = (song: SongsTableRow) => {
        setSongRowToDelete(song);
        setIsDeleteModalOpen(true);
    };

    const songsTableMeta: SongsTableMeta = {
        isLoading,
        hasError: isError,
        onEditSong: onClickEditSong,
        onDeleteSong: onClickDeleteSong,
        isMusicSongLoading: musicSongs.isLoadingFor,
        isTimeSeriesLoading: timeSeries.isLoadingFor,
        isTimeSeriesError: timeSeries.isErrorFor,
    };

    const songStatusOptions = [
        { value: 'released', label: 'Released' },
        { value: 'unreleased', label: 'Unreleased' },
    ];

    const songStatusSelectStyles: StylesConfig = {
        container: (base) => ({
            ...base,
            width: '18rem',
        }),
        control: (base) => ({
            ...base,
            height: '2.1875rem',
        }),
        option: (base) => ({
            ...base,
            color: '#242424',
        }),
        singleValue: (base) => ({
            ...base,
            color: '#242424',
        }),
    };

    return (
        <>
            <div className={styles.container}>
                <header className={styles.header}>
                    <PageTitle>Songs</PageTitle>
                </header>

                <menu className={styles.menu}>
                    <SearchInput
                        className={styles.searchInput}
                        value={urlState.search}
                        onChange={(val) => setUrlState({ search: val })}
                    />
                    <Select
                        className={styles.statusFilter}
                        options={songStatusOptions}
                        value={songStatusOptions.find((option) => option.value === urlState.status) ?? null}
                        onChange={(option) => setUrlState({ status: option?.value })}
                        placeholder="Select status..."
                        styles={songStatusSelectStyles}
                        isClearable
                        isSearchable={false}
                    />

                    <Button
                        className={styles.addSong}
                        appearance="primary"
                        onClick={() => setIsCreateModalOpen(true)}
                        iconLeft={<PlusIcon className={styles.plusIcon} />}
                    >
                        Song
                    </Button>
                </menu>

                <ScrollView className={styles.tableContainer}>
                    <SongsTable
                        data={rows}
                        meta={songsTableMeta}
                        onRowClick={(row) => setUrlState({ songModal: row.original.id })}
                        onEndReached={() => {
                            if (hasNextPage && !isFetchingNextPage) {
                                void fetchNextPage();
                            }
                        }}
                    />
                </ScrollView>
            </div>

            <CreateSongModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} />

            <UpdateSongModal
                isOpen={isUpdateModalOpen}
                onClose={() => {
                    setIsUpdateModalOpen(false);
                    setSelectedSongRowToEdit(null);
                }}
                song={selectedSongRowToEdit}
                musicSong={selectedSongRowToEdit?.musicSong ?? null}
            />

            <DeleteSongModal
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false);
                    setSongRowToDelete(null);
                }}
                song={songRowToDelete}
                musicSong={songRowToDelete?.musicSong ?? null}
            />

            <SongDataModal
                key={songIdForModal}
                isOpen={typeof songIdForModal === 'number'}
                onClose={() => {
                    setUrlState({ songModal: undefined });
                }}
                songId={songIdForModal}
            />
        </>
    );
};

export default SongsList;
